import React, { useState } from "react";
import styled from "styled-components";
import { useProfileConfig, useSourceColumns } from "../service";
import { SidePanel } from "../ui";
import { WarningsDetails } from "../editors";

/**
 * Compact parsed-source preview for the sidebar — a "did we read the file I expected?" check.
 * Column names by default; sample values behind a toggle so a wide source doesn't force a heavy
 * render every visit.
 */

const Rows = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  max-height: 300px;
  overflow: auto;
`;

const Row = styled.div<{ $samples: boolean }>`
  display: grid;
  grid-template-columns: ${(p) => (p.$samples ? "1fr 1fr" : "1fr")};
  gap: 10px;
  padding: 5px 0;
  font-size: 12.5px;
  border-bottom: 1px solid var(--miLine);

  .name {
    color: var(--miInk);
    font-weight: 500;
    word-break: break-word;
  }
  .val {
    color: var(--miInkFaint);
    font-family: ui-monospace, Menlo, monospace;
    font-size: 11.5px;
    word-break: break-word;
  }
  &:last-child {
    border-bottom: none;
  }
`;

const Note = styled.div`
  color: var(--miInkFaint);
  font-size: 12.5px;
  padding: 2px 0;
`;

export default function SourcePreviewPanel({ id }: { id: string }) {
  const [samples, setSamples] = useState(false);
  const cfg = useProfileConfig(id);
  const columns = useSourceColumns(
    cfg.data?.source.assetId || undefined,
    cfg.data?.source.format,
    cfg.data?.source.adapterConfig
  );
  const data = columns.data;
  const count = data?.columns.length ?? 0;

  return (
    <SidePanel>
      <header>
        Preview
        {count > 0 && (
          <button
            type="button"
            onClick={() => setSamples((v) => !v)}
            style={{
              background: "none",
              border: "none",
              padding: 0,
              cursor: "pointer",
              font: "inherit",
              letterSpacing: "inherit",
              color: "var(--miAccentBright)",
            }}
          >
            {samples ? "Hide values" : "Show values"}
          </button>
        )}
      </header>
      <div className="body">
        {!cfg.data?.source.assetId ? (
          <Note>No source selected.</Note>
        ) : columns.isFetching && !data ? (
          <Note>Parsing source…</Note>
        ) : columns.isError ? (
          <Note>Could not read this source from iconik.</Note>
        ) : data ? (
          <>
            <Note style={{ marginBottom: 8 }}>
              {count} column{count === 1 ? "" : "s"} · {data.recordCount} record
              {data.recordCount === 1 ? "" : "s"}
            </Note>
            <Rows>
              {data.columns.map((c) => (
                <Row key={c.name} $samples={samples}>
                  <span className="name">{c.name}</span>
                  {samples && <span className="val">{c.sample || "—"}</span>}
                </Row>
              ))}
            </Rows>
            {data.warnings.length > 0 && (
              <WarningsDetails warnings={data.warnings} />
            )}
          </>
        ) : (
          <Note>No preview available.</Note>
        )}
      </div>
    </SidePanel>
  );
}
